import { useState } from 'react';
import { Download, ExternalLink, Eye, FileArchive, FileText, Link2, type LucideIcon } from 'lucide-react';
import { VisionneusePdf } from './VisionneusePdf';
import { tailleRessource, urlRessource, type NatureRessource, type RessourceEnLecture, type VueLecon } from '../../lib/useLecteur';

const NATURES: Record<NatureRessource, { libelle: string; icone: LucideIcon; pastille: string }> = {
  PDF: { libelle: 'PDF', icone: FileText, pastille: 'bg-red-100 text-red-700' },
  ZIP: { libelle: 'ZIP', icone: FileArchive, pastille: 'bg-amber-100 text-amber-700' },
  LIEN: { libelle: 'LIEN', icone: Link2, pastille: 'bg-gray-100 text-gray-700' },
};

const ACTION =
  'inline-flex min-h-[36px] min-w-[36px] shrink-0 items-center justify-center gap-1.5 rounded-lg px-2 text-xs font-semibold text-codialis-sky transition hover:bg-codialis-sky/10 [@media(pointer:coarse)]:min-h-[40px] [@media(pointer:coarse)]:min-w-[40px]';

function LigneRessource({ ressource, consulter }: { ressource: RessourceEnLecture; consulter: () => void }): JSX.Element {
  const nature = NATURES[ressource.nature];
  const Icone = nature.icone;
  const detail = ressource.nature === 'LIEN' ? ressource.domaine : tailleRessource(ressource.tailleOctets);
  return (
    <li className="flex items-center gap-3 rounded-xl border border-gray-100 p-2 transition hover:border-codialis-mint/40 hover:bg-gray-50 sm:p-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-50">
        <Icone className="h-5 w-5 text-codialis-navy" aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-codialis-navy">{ressource.titre}</div>
        <div className="truncate text-xs text-codialis-gray">{detail}</div>
      </div>
      <span className={`hidden shrink-0 rounded px-2 py-0.5 text-[10px] font-bold sm:inline ${nature.pastille}`}>{nature.libelle}</span>
      {ressource.nature === 'PDF' && (
        <button type="button" onClick={consulter} className={ACTION} aria-label={`Consulter « ${ressource.titre} »`}>
          <Eye className="h-4 w-4" aria-hidden />
          <span className="hidden sm:inline">Consulter</span>
        </button>
      )}
      {ressource.nature === 'LIEN' && ressource.adresse ? (
        <a
          href={ressource.adresse}
          target="_blank"
          rel="noopener noreferrer"
          className={ACTION}
          aria-label={`Ouvrir « ${ressource.titre} » dans un nouvel onglet`}
        >
          <ExternalLink className="h-4 w-4" aria-hidden />
          <span className="hidden sm:inline">Ouvrir</span>
        </a>
      ) : (
        <a
          href={urlRessource(ressource.id)}
          download={ressource.nomFichier ?? true}
          className={ACTION}
          aria-label={`Télécharger « ${ressource.titre} »`}
        >
          <Download className="h-4 w-4" aria-hidden />
          <span className="hidden sm:inline">Télécharger</span>
        </a>
      )}
    </li>
  );
}

/**
 * Onglet « Ressources » du lecteur (TF-447), sur le modèle de la maquette :
 * les ressources de la leçon, dans l'ordre où l'auteur les a jointes. Un PDF
 * se consulte dans le lecteur sans être téléchargé, PDF et ZIP se
 * téléchargent, un lien s'ouvre dans un nouvel onglet. Chaque fichier passe
 * par la route gardée du lecteur, qui trace la lecture.
 */
export function RessourcesLecon({ vue }: { vue: VueLecon }): JSX.Element {
  const [ouverte, setOuverte] = useState<RessourceEnLecture | null>(null);
  const { ressources } = vue.lecon;

  return (
    <>
      <h3 className="font-display mb-1 text-xl font-bold text-codialis-navy">Ressources de la leçon</h3>
      {vue.acces === 'APERCU' && (
        <p className="mb-3 text-xs text-codialis-gray">
          Aperçu : les ressources se joignent à l’étape « Contenus & supports » du cours.
        </p>
      )}
      {ressources.length === 0 ? (
        <p className="mt-3 rounded-xl bg-gray-50 px-4 py-3 text-sm text-codialis-gray">
          Aucune ressource n’est jointe à cette leçon.
        </p>
      ) : (
        <ul className="mt-3 space-y-2">
          {ressources.map((ressource) => (
            <LigneRessource key={ressource.id} ressource={ressource} consulter={() => setOuverte(ressource)} />
          ))}
        </ul>
      )}
      {ouverte && (
        <VisionneusePdf
          titre={ouverte.titre}
          nomFichier={ouverte.nomFichier ?? `${ouverte.titre}.pdf`}
          source={urlRessource(ouverte.id, true)}
          telechargement={urlRessource(ouverte.id)}
          onFermer={() => setOuverte(null)}
        />
      )}
    </>
  );
}
